/**
 * Minimal syntax highlighter for the API ad's code windows — a trimmed port of
 * the one on prefetch.io, so the snippets read the same on both sites.
 *
 * Deliberately not a parser: it walks the source once, and at every position
 * tries a short ordered list of token rules for the language. The snippets are
 * a dozen lines at most, so the scan is cheap, and a keyword inside a string
 * can't be lit because the string rule consumes it first.
 *
 * Emits `tok-*` class names rather than Tailwind utilities: `utils/` is outside
 * Tailwind's content globs, so utilities written here would never be generated.
 * The colours live with the window that renders them (atoms/CodeWindow.vue).
 *
 * The input is escaped before any markup is added, so the result is safe to
 * pass to v-html.
 */

export type CodeLanguage = 'bash' | 'json' | 'prompt'

interface Rule {
  cls: string
  /** Sticky, so it only ever matches AT the cursor rather than after it. */
  re: RegExp
  /** Identifier-like rules must not fire mid-word (`nullable` is not `null`). */
  word?: boolean
  /** Rules run over this rule's own match — a shell string interpolates. */
  nested?: readonly Rule[]
}

const STRING: Rule = { cls: 'tok-string', re: /"(?:\\.|[^"\\\n])*"|'(?:\\.|[^'\\\n])*'/y }
const NUMBER: Rule = { cls: 'tok-number', re: /\d+(?:\.\d+)?\b/y, word: true }
const HASH_COMMENT: Rule = { cls: 'tok-comment', re: /#[^\n]*/y }
const SLASH_COMMENT: Rule = { cls: 'tok-comment', re: /\/\/[^\n]*/y }
const URL: Rule = { cls: 'tok-accent', re: /https?:\/\/[^\s,)"'`]+/y }
/** `$PREFETCH_API_KEY` — the one thing a reader has to swap out. */
const SHELL_VAR: Rule = { cls: 'tok-accent', re: /\$[A-Za-z_][A-Za-z0-9_]*/y }
/** A key is a string followed by a colon; checked before STRING so it wins. */
const JSON_KEY: Rule = { cls: 'tok-key', re: /"(?:\\.|[^"\\\n])*"(?=\s*:)/y }

function keywords(words: string): Rule {
  return { cls: 'tok-keyword', re: new RegExp(`(?:${words})\\b`, 'y'), word: true }
}

const RULES: Record<CodeLanguage, readonly Rule[]> = {
  bash: [HASH_COMMENT, { ...STRING, nested: [SHELL_VAR] }, URL, SHELL_VAR, keywords('claude|codex|curl'), NUMBER],
  // A config's endpoint is the one value a reader has to copy, and it lives
  // inside a quoted string — so a string here lights a URL the way prose does.
  json: [SLASH_COMMENT, JSON_KEY, { ...STRING, nested: [URL] }, keywords('true|false|null'), NUMBER],
  // Prose, not code: only the parts the reader acts on are lit.
  prompt: [HASH_COMMENT, URL, SHELL_VAR],
}

function escapeHtml(text: string): string {
  return text.replace(/[&<>]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : '&gt;'))
}

function isWordChar(char: string | undefined): boolean {
  return char !== undefined && /[\w$]/.test(char)
}

function render(source: string, rules: readonly Rule[]): string {
  let out = ''
  let plain = ''
  let i = 0

  while (i < source.length) {
    let matched = false

    for (const rule of rules) {
      if (rule.word && isWordChar(source[i - 1])) continue
      rule.re.lastIndex = i
      const m = rule.re.exec(source)
      if (!m || m.index !== i) continue

      if (plain) { out += escapeHtml(plain); plain = '' }
      const body = rule.nested ? render(m[0], rule.nested) : escapeHtml(m[0])
      out += `<span class="${rule.cls}">${body}</span>`
      i += m[0].length
      matched = true
      break
    }

    if (!matched) {
      plain += source[i]
      i += 1
    }
  }

  return out + escapeHtml(plain)
}

export function highlightCode(source: string, language: CodeLanguage): string {
  return render(source, RULES[language])
}
